import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterValue } from "../components/types/types";

export const NOTE_MIN = 2.9;
export const NOTE_MAX = 5;
export const NOTE_STEP = 0.1;
const normalizeNote = (value: number) => Math.round(value * 10) / 10;
export const getNoteFilterLabel = (value: number) => {
    const note = normalizeNote(value);
    if (note <= NOTE_MIN) return null;
    if (note === 5) return "5 étoiles";
    return `${note.toFixed(1).replace(".0", "")} étoiles et plus`;
};
export const isNoteFilter = (value: FilterValue): value is string => {
    if (typeof value !== "string") return false;
    return (
        /^\d(?:\.\d)? étoiles$/.test(value) ||
        /^\d(?:\.\d)? étoiles et plus$/.test(value)
    );
};
export const getNoteValueFromFilter = (value: string): number | null => {
    if (!isNoteFilter(value)) return null;
    const match = value.match(/(\d(?:\.\d)?)/);
    if (!match) return null;
    return Number(match[1]);
};
export const getSelectedNoteValue = (filters: FilterValue[]): number => {
    const filter = filters.find(isNoteFilter);
    if (!filter) return NOTE_MIN;
    return getNoteValueFromFilter(filter) ?? NOTE_MIN;
};
export const getNotePercentage = (value: number) => {
    const normalized = normalizeNote(value);
    return (((normalized - NOTE_MIN) / (NOTE_MAX - NOTE_MIN)) *100);
};
export const getNoteVisualPercentage = (value: number) => {
    const percentage = getNotePercentage(value);
    const EDGE_OFFSET = 3;
    return EDGE_OFFSET + percentage * ((100 - EDGE_OFFSET * 2) / 100);
};
export const getNoteLabelPercentage = (value: number) => {
    const percentage = getNoteVisualPercentage(value);
    if (value >= NOTE_MAX) return percentage;

    const MIN_LABEL_PERCENTAGE = 10;
    const MAX_LABEL_PERCENTAGE = 82;
    return Math.max(
        MIN_LABEL_PERCENTAGE,
        Math.min(MAX_LABEL_PERCENTAGE, percentage)
    );

};

interface UseNoteFilterParams {
    listFilter: FilterValue[];
    applyNoteFilters: (
        previousValues: FilterValue[],
        selectedValues: FilterValue[]
    ) => void;
}

export const useNoteFilter = ({
    listFilter,
    applyNoteFilters
}: UseNoteFilterParams) => {
    const selectedNote = useMemo(() => getSelectedNoteValue(listFilter),[listFilter]);
    const hasDraggedRef = useRef(false);

    const [noteValue, setNoteValue] = useState(selectedNote);
    const [showCheckNote, setShowCheckNote] = useState(false);

    const applyTimerRef = useRef<number | null>(null);
    const checkTimerRef = useRef<number | null>(null);

    const noteWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number;} | null>(null);
    const touchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);

    useEffect(() => {
        setNoteValue(previous =>
            previous === selectedNote ? previous : selectedNote
        );
    }, [selectedNote]);

    const triggerCheckAnimation = useCallback(() => {
        setShowCheckNote(true);
        if (checkTimerRef.current) {
            window.clearTimeout(checkTimerRef.current);
        }
        checkTimerRef.current = window.setTimeout(() => {
            setShowCheckNote(false);
            checkTimerRef.current = null;
        }, 1500);
    }, []);

    const applyNote = useCallback( (value: number) => {
        const normalizedValue = normalizeNote(value);
        const previousNoteFilters = listFilter.filter(isNoteFilter);
        if (normalizedValue <= NOTE_MIN) {
            applyNoteFilters(previousNoteFilters,[]);
            return;
        }
        const label = getNoteFilterLabel(normalizedValue);
        if (!label) {
            applyNoteFilters(previousNoteFilters,[]);
            return;
        }
        applyNoteFilters(previousNoteFilters,[label]);
    },[listFilter, applyNoteFilters]);

    const scheduleApply = useCallback((value: number, delay = 650) => {
            if (applyTimerRef.current) {
                window.clearTimeout(applyTimerRef.current);
            }
            applyTimerRef.current =
                window.setTimeout(() => {
                    applyTimerRef.current = null;
                    applyNote(value);
                    triggerCheckAnimation();
                }, delay);
    },[applyNote, triggerCheckAnimation]);
    const handleNoteChange = useCallback(( e: React.ChangeEvent<HTMLInputElement>) => {
            const value = normalizeNote(Number(e.target.value));

            setNoteValue(value);
            scheduleApply(value);
    },[scheduleApply]);
    useEffect(() => {
        return () => {
            if (applyTimerRef.current) {
                window.clearTimeout( applyTimerRef.current);
            }
            if (checkTimerRef.current) {
                window.clearTimeout(checkTimerRef.current);
            }
        };
    }, []);
    const notePercentage = useMemo(
        () => getNotePercentage(noteValue),
    [noteValue]);
    const noteVisualPercentage = useMemo(
        () => getNoteVisualPercentage(noteValue),
    [noteValue]);
    const noteLabelPercentage = useMemo(
        () => getNoteLabelPercentage(noteValue),
    [noteValue]);
    const noteLabel = useMemo(
        () => getNoteFilterLabel(noteValue) ?? "Toutes les notes",
    [noteValue]);

    const updateNoteFromClientX = useCallback((clientX: number, delay = 450) => {
        const wrapper = noteWrapperRef.current;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();

        const x = clientX - rect.left;
        const ratio = Math.max(
            0,
            Math.min(1, x / rect.width)
        );
        const raw = NOTE_MIN + ratio * (NOTE_MAX - NOTE_MIN);
        const stepped = Math.round((raw - NOTE_MIN) / NOTE_STEP ) *NOTE_STEP + NOTE_MIN;
        const nextValue = normalizeNote( Math.max(NOTE_MIN,Math.min(NOTE_MAX, stepped) )
        );

        setNoteValue(nextValue);
        scheduleApply(nextValue, delay);
    },[scheduleApply]);
    const updateNoteFromTouch = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;

        updateNoteFromClientX(
            touch.clientX,
            450
        );
    },[updateNoteFromClientX]);
    const handleNoteClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
         if (hasDraggedRef.current) {
            hasDraggedRef.current = false;
            return;
        }
        updateNoteFromClientX(e.clientX,0);
    },[updateNoteFromClientX]);
    const handleNoteTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;
        touchStartRef.current = {
            x: touch.clientX,
            y: touch.clientY
        };

        touchDirectionRef.current = null;
        hasDraggedRef.current = false;
    },[]);
    const handleNoteTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const start = touchStartRef.current;
        if (!start) return;
        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;

        if (touchDirectionRef.current === null &&Math.abs(deltaX) < 6 &&Math.abs(deltaY) < 6) {
            return;
        }

        if (touchDirectionRef.current === null) {
            touchDirectionRef.current =
                Math.abs(deltaX) > Math.abs(deltaY)
                    ? "horizontal"
                    : "vertical";
        }
        if ( touchDirectionRef.current === "vertical") {
            return;
        }
        hasDraggedRef.current = true;
        e.preventDefault();
        e.stopPropagation();
        updateNoteFromTouch(e);
    }, [updateNoteFromTouch]);
    const handleNoteTouchEnd = useCallback(() => {
        touchStartRef.current = null;
        touchDirectionRef.current = null;
    }, []);

    return {noteValue,noteLabel,notePercentage,noteVisualPercentage,noteLabelPercentage,showCheckNote,noteWrapperRef,handleNoteClick,handleNoteChange,handleNoteTouchStart,handleNoteTouchMove,handleNoteTouchEnd};
};

export const matchesNoteFilter = (filter: FilterValue,restaurantNote: number): boolean | null => {
    if (!isNoteFilter(filter)) return null;
    const minimumNote = getNoteValueFromFilter(filter);
    if (minimumNote === null) return null;
    return restaurantNote >= minimumNote;
};